import classNames from 'classnames';
import { ConnectedRouter } from 'connected-next-router';
import Router from 'next/router';
import { memo, type ReactNode } from 'react';

import { MobileNavigationContextProvider } from '@air/component-mobile-navigation';
import { ActionBarProvider } from '@air/provider-action-bar';
import { useBreakpointsContext } from '@air/provider-media-query';
import { ModalProvider } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { SysadminProvider } from '@air/sysadmin';
import { ErrorBoundary } from '@air/utils-error';

import { AccountProvisionLayout } from '~/components/AccountProvisioner/components/AccountProvisionLayout';
import { AccountProvisionerProvider } from '~/components/AccountProvisioner/providers/AccountProvisionerProvider';
import { DevModalProvider } from '~/components/DevModal/DevModalProvider';
import { Banners } from '~/components/Layouts/HomeLayout/Banners';
import { useCheckIfUserHasWorkspaces } from '~/components/Layouts/HomeLayout/hooks/useCheckIfUserHasWorkspaces';
import { useConfigureThirdPartyServices } from '~/components/Layouts/HomeLayout/hooks/useConfigureThirdPartyServices';
import { useCreateBoardFromCanvasSearchParams } from '~/components/Layouts/HomeLayout/hooks/useCreateBoardFromCanvasSearchParams';
import { useCreateCanvasFromSearchParams } from '~/components/Layouts/HomeLayout/hooks/useCreateCanvasFromSearchParams';
import { useShowDemoFormModal } from '~/components/Layouts/HomeLayout/hooks/useDemoFormModal';
import { useHandleConfigureSignoutUrl } from '~/components/Layouts/HomeLayout/hooks/useHandleConfigureSignoutUrl.ts';
import { useHandleInactivity } from '~/components/Layouts/HomeLayout/hooks/useHandleInactivity';
import { useHandleSkipOnboarding } from '~/components/Layouts/HomeLayout/hooks/useHandleSkipOnboarding';
import { useHandleUnauthenticatedHomeLayoutUser } from '~/components/Layouts/HomeLayout/hooks/useHandleUnauthenticatedHomeLayoutUser.ts';
import { useHandleUserNeedsRedirect } from '~/components/Layouts/HomeLayout/hooks/useHandleUserNeedsRedirect';
import { useNotificationsEvent } from '~/components/Layouts/HomeLayout/hooks/useNotificationsEvent';
import { SideNav } from '~/components/Layouts/HomeLayout/SideNav/SideNav';
import { SkipOnboardingLoadingOverlay } from '~/components/Layouts/HomeLayout/SkipOnboardingLoadingOverlay';
import { MaintenanceMode } from '~/components/MaintenanceMode/MaintenanceMode';
import { CoreMobileNavigation } from '~/components/Mobile/CoreMobileNavigation';
import { PrivateDropzone } from '~/components/PrivateDropzone/PrivateDropzone';
import { PrivatePageTitle } from '~/components/PrivatePageTitle';
import { PrivateReduxProvider } from '~/components/PrivateReduxProvider';
import { PrivateSocketContextProvider } from '~/components/PrivateSocketContextProvider';
import { PrivateUploadsActivityContainer } from '~/components/PrivateUploadsActivityContainer';
import { PrivateWorkspaceActivityContainer } from '~/components/PrivateWorkspaceActivityContainer';
import { ReactDndDragLayer } from '~/components/ReactDndDragLayer/ReactDndDragLayer';
import { SelectedItemsHydration } from '~/components/SelectedItemsHydration';
import { MainContent } from '~/components/Shared/ClipGallery/Content';
import { TryCanvasPopoverMobileWrapper } from '~/components/TryCanvasPopover/TryCanvasPopoverMobileWrapper';
import { TryCanvasPopoverWrapper } from '~/components/TryCanvasPopover/TryCanvasPopoverWrapper';
import { KanbanOffsetContextProvider } from '~/components/v2/components/shared/kanban/KanbanOffsetContextProvider';
import { TableHeaderPositionContextProvider } from '~/components/v2/components/shared/table/TableHeaderPositionContextProvider';
import { useShowUI2Page } from '~/components/v2/hooks/useShowUI2Page';
import { useTailwindFlag } from '~/components/v2/hooks/useTailwindFlag';
import { PrivateDefaultLayout } from '~/components/v2/layouts/PrivateDefaultLayout';
import { useShowFirstUploadPromptValue } from '~/components/v2/store/features/onboarding/useOnboardingPreferencesStore';
import { WorkspaceLockedOverlay } from '~/components/WorkspaceLockedOverlay/WorkspaceLockedOverlay';
import { WorkspaceOnboardingUploadPrompt } from '~/components/WorkspaceOnboardingForm/WorkspaceOnboardingUploadPrompt';
import { Routes } from '~/constants/routes';
import { MAIN } from '~/constants/testIDs';
import { inter } from '~/fonts/inter';
import { useOnLocationChange } from '~/hooks/router/useOnLocationChange';
import { BillingCycleProvider } from '~/hooks/useBillingCycle';
import { useBroadcastCreditStats } from '~/hooks/useBroadcastCreditStats';
import { useHandleSessionExpired } from '~/hooks/useHandleSessionExpired';
import { useHandleWorkspaceAddOnsUpdated } from '~/hooks/useHandleWorkspaceAddOnsUpdated';
import { useHandleWorkspaceCreditsConsumed } from '~/hooks/useHandleWorkspaceCreditsConsumed';
import { ROOT_ELEMENT_ID, SELECTION_AREA_ELEMENT_ID } from '~/hooks/useMouseSelection';
import { ThumbnailPreferenceContextProvider } from '~/hooks/useThumbnailPreference';
import { AccountProvider } from '~/providers/AccountProvider';
import { CannyProvider } from '~/providers/CannyProvider';
import { CurrentWorkspaceMemberProvider } from '~/providers/CurrentWorkspaceMemberProvider';
import { CurrentWorkspacePermissionsProvider } from '~/providers/CurrentWorkspacePermissionsProvider';
import { CurrentWorkspaceProvider, useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { DesktopAppConnectionProvider } from '~/providers/DesktopAppConnectionProvider';
import { FiltersProvider, type GetImmutableFilters } from '~/providers/FiltersProvider';
import { InactivityProvider } from '~/providers/InactivityProvider';
import { ModifiersKeysPressedProvider } from '~/providers/ModifiersKeysPressedProvider';
import { PlanProvider } from '~/providers/PlanProvider';
import { PrivateCustomFieldsProvider } from '~/providers/PrivateCustomFieldsProvider';
import { PrivateSearchProvider } from '~/providers/PrivateSearchProvider';
import { SearchHeaderHeightProvider } from '~/providers/SearchHeaderHeightProvider';
import { SideNavProvider } from '~/providers/SideNavProvider';
import { SubscriptionPlanProvider } from '~/providers/SubscriptionPlanProvider';
import { SubscriptionProvider } from '~/providers/SubscriptionProvider';
import { WorkspaceMembersProvider } from '~/providers/WorkspaceMembersProvider';
import { WorkspaceRolesProvider } from '~/providers/WorkspaceRolesProvider';

type HomeLayoutProps = {
  bottomBar?: ReactNode;
  children: ReactNode;
  forceV1?: boolean;
  getLibraryActions?: boolean;
  headerActions?: ReactNode;
  search?: ReactNode;
};

const HomeLayout = memo(
  ({ bottomBar, children, forceV1, getLibraryActions, headerActions, search }: HomeLayoutProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { dismissAllToasts } = useToasts();
    const { showUI2 } = useShowUI2Page();
    const showWorkspaceOnboardingUploadPrompt = useShowFirstUploadPromptValue(currentWorkspace?.id);
    const { isLoading: isSkipOnboardingLoading } = useHandleSkipOnboarding();

    useHandleConfigureSignoutUrl();
    useHandleInactivity();
    useCheckIfUserHasWorkspaces();
    useCreateCanvasFromSearchParams();
    useCreateBoardFromCanvasSearchParams();
    useHandleUserNeedsRedirect();
    useHandleUnauthenticatedHomeLayoutUser();
    useShowDemoFormModal();
    useNotificationsEvent();
    useHandleWorkspaceAddOnsUpdated();
    useHandleSessionExpired();
    useHandleWorkspaceCreditsConsumed();
    useBroadcastCreditStats();
    useOnLocationChange(dismissAllToasts);
    useConfigureThirdPartyServices();
    useTailwindFlag();

    if (showUI2 && !forceV1) {
      return (
        <>
          <PrivateDefaultLayout
            bottomBar={bottomBar}
            search={search}
            headerActions={headerActions}
            getLibraryActions={getLibraryActions}
          >
            {children}

            <PrivateWorkspaceActivityContainer />
            <PrivateUploadsActivityContainer />
          </PrivateDefaultLayout>
          {showWorkspaceOnboardingUploadPrompt && <WorkspaceOnboardingUploadPrompt />}
          {isSkipOnboardingLoading && <SkipOnboardingLoadingOverlay />}
          {isAboveMediumScreen && <TryCanvasPopoverWrapper />}
        </>
      );
    }

    return (
      <div className="relative flex h-full overflow-y-hidden">
        <PrivatePageTitle title={currentWorkspace?.name} />

        {isAboveMediumScreen && <SideNav />}
        <MainContent className="bg-grey-1" id={SELECTION_AREA_ELEMENT_ID} data-testid={MAIN}>
          {currentWorkspace && <Banners />}
          {children}
        </MainContent>

        {!isAboveMediumScreen && <CoreMobileNavigation />}

        <PrivateWorkspaceActivityContainer />
        <PrivateUploadsActivityContainer />
        {showWorkspaceOnboardingUploadPrompt && <WorkspaceOnboardingUploadPrompt />}
        {isSkipOnboardingLoading && <SkipOnboardingLoadingOverlay />}
        {!isAboveMediumScreen && <TryCanvasPopoverMobileWrapper />}
      </div>
    );
  },
);

HomeLayout.displayName = 'HomeLayout';

export const getLayout = (
  page: ReactNode,
  {
    bottomBar,
    getImmutableFilters = () => ({}),
    search,
    supportsSmartSearch = false,
    headerActions,
    getLibraryActions,
    forceV1,
  }: {
    bottomBar?: ReactNode;
    getImmutableFilters?: GetImmutableFilters;
    search?: ReactNode;
    supportsSmartSearch?: boolean;
    headerActions?: ReactNode;
    getLibraryActions?: boolean;
    forceV1?: boolean;
  } = {},
) => (
  <div className={classNames('h-full', inter.className, inter.variable)} id={ROOT_ELEMENT_ID}>
    <AccountProvisionerProvider>
      <AccountProvisionLayout>
        <AccountProvider>
          <PrivateReduxProvider>
            <ErrorBoundary>
              <ConnectedRouter>
                <SysadminProvider onRedirectOnWorkspaceChange={() => Router.push(Routes.media.all)}>
                  <BillingCycleProvider>
                    <CurrentWorkspaceProvider>
                      <CurrentWorkspacePermissionsProvider>
                        <SearchHeaderHeightProvider>
                          <SubscriptionProvider>
                            <WorkspaceMembersProvider>
                              <WorkspaceRolesProvider>
                                <CurrentWorkspaceMemberProvider>
                                  <PrivateCustomFieldsProvider>
                                    <BillingCycleProvider>
                                      <InactivityProvider>
                                        <FiltersProvider getImmutableFilters={getImmutableFilters}>
                                          <ModifiersKeysPressedProvider>
                                            <ThumbnailPreferenceContextProvider>
                                              <ModalProvider>
                                                <DevModalProvider>
                                                  <PrivateSearchProvider supportsSmartSearch={supportsSmartSearch}>
                                                    <PrivateDropzone>
                                                      <PrivateSocketContextProvider>
                                                        <SubscriptionPlanProvider>
                                                          <PlanProvider>
                                                            <CannyProvider>
                                                              <DesktopAppConnectionProvider>
                                                                <ActionBarProvider>
                                                                  <SideNavProvider>
                                                                    <MobileNavigationContextProvider>
                                                                      <MaintenanceMode>
                                                                        <WorkspaceLockedOverlay>
                                                                          <TableHeaderPositionContextProvider>
                                                                            <KanbanOffsetContextProvider>
                                                                              <HomeLayout
                                                                                bottomBar={bottomBar}
                                                                                search={search}
                                                                                headerActions={headerActions}
                                                                                getLibraryActions={getLibraryActions}
                                                                                forceV1={forceV1}
                                                                              >
                                                                                {page}
                                                                              </HomeLayout>
                                                                            </KanbanOffsetContextProvider>
                                                                          </TableHeaderPositionContextProvider>
                                                                        </WorkspaceLockedOverlay>
                                                                      </MaintenanceMode>
                                                                    </MobileNavigationContextProvider>
                                                                  </SideNavProvider>
                                                                </ActionBarProvider>
                                                              </DesktopAppConnectionProvider>
                                                            </CannyProvider>
                                                          </PlanProvider>
                                                        </SubscriptionPlanProvider>
                                                      </PrivateSocketContextProvider>
                                                    </PrivateDropzone>
                                                  </PrivateSearchProvider>
                                                </DevModalProvider>
                                                <ReactDndDragLayer />
                                              </ModalProvider>
                                            </ThumbnailPreferenceContextProvider>
                                          </ModifiersKeysPressedProvider>
                                        </FiltersProvider>
                                      </InactivityProvider>
                                    </BillingCycleProvider>
                                  </PrivateCustomFieldsProvider>
                                </CurrentWorkspaceMemberProvider>
                              </WorkspaceRolesProvider>
                            </WorkspaceMembersProvider>
                          </SubscriptionProvider>
                        </SearchHeaderHeightProvider>
                      </CurrentWorkspacePermissionsProvider>
                      <SelectedItemsHydration />
                    </CurrentWorkspaceProvider>
                  </BillingCycleProvider>
                </SysadminProvider>
              </ConnectedRouter>
            </ErrorBoundary>
          </PrivateReduxProvider>
        </AccountProvider>
      </AccountProvisionLayout>
    </AccountProvisionerProvider>
  </div>
);
